import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout/Layout';
import { CatalogsProvider, useCatalogs } from '../contexts/CatalogsProvider';
import ContactSearchBar from '../components/incidents/ContactSearchBar';
import ContactResultsList from '../components/incidents/ContactResultsList';
import ContactCreateDrawer from '../components/incidents/ContactCreateDrawer';
import IncidentClassification from '../components/incidents/IncidentClassification';
import IncidentSummary from '../components/incidents/IncidentSummary';
import IncidentEditForm from '../components/incidents/IncidentEditForm';
import SectionCards from '../components/incidents/SectionCards';
import FormRenderer from '../components/form/FormRenderer';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import incidentsService from '../services/incidents';
import contactsService from '../services/contacts';
import apiClient from '../services/apiClient';
import { getUsers } from '../services/users';
import Skeleton, { SkeletonButton } from '../components/common/Skeleton/Skeleton';
import './Incidentes.scss';
import '../components/incidents/IncidentComponents.scss';

const IncidentesContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('edit');
  const contactIdFromUrl = searchParams.get('contact_id');
  const isEditMode = !!editId;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingIncident, setLoadingIncident] = useState(isEditMode);
  const [incidentData, setIncidentData] = useState({
    // Paso 1 - Contacto
    contact_id: null,
    selectedContact: null,
    searchQuery: '',
    searchResults: [],
    isSearching: false,
    
    // Paso 2 - Clasificación
    category_id: null,
    form_id: null,
    channel_id: 'web',
    // Guardar los nombres seleccionados para el subject
    category_name: '',
    form_name: '',
    
    // Paso 3 - Datos específicos
    comments: '',
    title: '',
    description: '',
    
    // Schema de campos dinámicos
    fieldsSchema: null,
    loadingFields: false,
    errorFields: null
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [originalTask, setOriginalTask] = useState(null); // Guardar el task completo original
  const [statuses, setStatuses] = useState([]); // Lista de status disponibles
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [statusId, setStatusId] = useState(null); // Status seleccionado para edición
  const [assignedTo, setAssignedTo] = useState(null); // Usuario responsable asignado
  const [users, setUsers] = useState([]); // Lista de usuarios disponibles
  const [loadingUsers, setLoadingUsers] = useState(false); // Estado de carga de usuarios
  const [priorities, setPriorities] = useState([]); 
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [priorityId, setPriorityId] = useState(null); 
  const [completedSections, setCompletedSections] = useState({});

  const selectedFormIdForUsers = isEditMode
    ? (originalTask && originalTask.form
      ? (typeof originalTask.form === 'object' ? originalTask.form.id : originalTask.form)
      : null)
    : incidentData.form_id;
  
  const { catalogs, loading, getCategoriesByParent, getFormsByCategory, getFieldsSchema } = useCatalogs();
  const { toasts, success, error: showError, removeToast } = useToast();

  // Cargar contacto desde URL si existe contact_id
  useEffect(() => {
    const loadContactFromUrl = async () => {
      // Solo cargar si hay contact_id en la URL y no estamos en modo edición
      if (!contactIdFromUrl || isEditMode) return;

      try {
        const contact = await contactsService.getContactById(contactIdFromUrl);
        
        // Establecer el contacto en el estado
        setIncidentData(prev => ({
          ...prev,
          contact_id: contact.id,
          selectedContact: contact
        }));
        
        // Avanzar automáticamente al paso 2
        setTimeout(() => {
          setCurrentStep(2);
        }, 300);
      } catch (error) {
        console.error('Error al cargar contacto desde URL:', error);
        showError(`Error al cargar el contacto: ${error.message || 'Contacto no encontrado'}`);
      }
    };

    loadContactFromUrl();
  }, [contactIdFromUrl, isEditMode, showError]);

  // Cargar lista de status disponibles (intentar, si no existe el endpoint el backend lo hará después)
  useEffect(() => {
    const loadStatuses = async () => {
      if (!isEditMode) return;
      
  try {
  setLoadingStatuses(true);
  // Use the task-specific statuses endpoint when editing a task
  const response = await apiClient.get(`/api/tasks/${editId}/statuses/`);
        // Asegurarse de que siempre sea un array
        let statusesList = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            statusesList = response.data;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            statusesList = response.data.results;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            statusesList = response.data.data;
          }
        }
        setStatuses(statusesList);
      } catch (error) {
        console.warn('No se pudo cargar la lista de status. El backend puede no tener el endpoint aún:', error);
        // No mostrar error al usuario, el backend lo implementará después
        setStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    loadStatuses();
  }, [isEditMode, editId]);

  useEffect(() => {
    const loadPriorities = async () => {
      if (!isEditMode) return;
      
      try {
        setLoadingPriorities(true);
        const response = await apiClient.get(`/api/tasks/priorities/`);
        let prioritiesList = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            prioritiesList = response.data;
          } else if (response.data.results && Array.isArray(response.data.results)) {
            prioritiesList = response.data.results;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            prioritiesList = response.data.data;
          }
        }
        setPriorities(prioritiesList);
      } catch (error) {
        console.warn('No se pudo cargar la lista de prioridades:', error);
        setPriorities([]);
      } finally {
        setLoadingPriorities(false);
      }
    };

    loadPriorities();
  }, [isEditMode]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!selectedFormIdForUsers) {
        setUsers([]);
        setLoadingUsers(false);
        return;
      }

      try {
        setLoadingUsers(true);
        const usersList = await getUsers(selectedFormIdForUsers);
        setUsers(usersList);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        showError('Error al cargar la lista de usuarios');
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, [showError, selectedFormIdForUsers]);

  // Cargar datos del incidente si estamos en modo edición
  useEffect(() => {
    const loadIncidentForEdit = async () => {
      if (!isEditMode || !editId) return;

      try {
        setLoadingIncident(true);
        const incident = await incidentsService.getIncidentById(editId);
        
        // Cargar el task completo para obtener los IDs reales
        const taskResponse = await apiClient.get(`/api/tasks/${editId}/`);
        const task = taskResponse.data || {};
        
        // Guardar el task completo original para poder hacer PUT con todos los datos
        setOriginalTask(task);
        
        // Extraer el status del task (puede venir como objeto o como ID)
        const taskStatus = task.status;
        let currentStatusId = null;
        if (taskStatus) {
          if (typeof taskStatus === 'object' && taskStatus.id) {
            currentStatusId = taskStatus.id;
          } else if (typeof taskStatus === 'object') {
            // Si viene como objeto pero sin id explícito, usar el objeto directamente
            // El backend debería devolver el id, pero por si acaso
            currentStatusId = taskStatus.id || taskStatus;
          } else {
            // Si viene como ID (número o string)
            currentStatusId = taskStatus;
          }
        }
        setStatusId(currentStatusId);

        // Extraer el assigned_to del task
        const taskAssignedTo = task.assigned_to;
        let currentAssignedToId = null;
        if (taskAssignedTo) {
          if (typeof taskAssignedTo === 'object' && taskAssignedTo.id) {
            currentAssignedToId = taskAssignedTo.id;
          } else {
            currentAssignedToId = taskAssignedTo;
          }
        }
        setAssignedTo(currentAssignedToId);

        const taskPriority = task.priority;
        let currentPriorityId = null;
        if (taskPriority) {
          if (typeof taskPriority === 'object' && taskPriority.id) {
            currentPriorityId = taskPriority.id;
          } else {
            currentPriorityId = taskPriority;
          }
        }
        setPriorityId(currentPriorityId);
        
        const categoryId = task.category
          ? (typeof task.category === 'object' ? task.category.id : task.category)
          : null;
        const formId = task.form
          ? (typeof task.form === 'object' ? task.form.id : task.form)
          : null;
        
        // Mapear los datos del incidente al formato del formulario
        setIncidentData(prev => ({
          ...prev,
          contact_id: task.contact ? (typeof task.contact === 'object' ? task.contact.id : task.contact) : (incident.contact?.id || null),
          selectedContact: incident.contact ? {
            id: incident.contact.id,
            first_name: incident.contact.first_name,
            last_name: incident.contact.last_name,
            cuil: incident.contact.cuil,
            email: incident.contact.email,
            phone: incident.contact.phone,
            phone2: incident.contact.phone2,
            address: incident.contact.address
          } : null,
          category_id: categoryId || null,
          form_id: formId || null,
          // Solo usar canal_codigo si es un código válido (no "No especificado" u otros valores inválidos)
          channel_id: (task.canal_codigo && 
                      task.canal_codigo !== '' && 
                      task.canal_codigo.toLowerCase() !== 'no especificado' &&
                      task.canal_codigo.toLowerCase() !== 'sin especificar') 
                      ? task.canal_codigo 
                      : (incident.channel && 
                         incident.channel !== 'No especificado' && 
                         incident.channel.toLowerCase() !== 'no especificado') 
                          ? incident.channel 
                          : null,
          category_name: incident.classification?.category || '',
          form_name: '',
          title: task.title || '',
          description: task.description || ''
        }));

        // Si hay categoria y form, cargar el schema de campos
        if (categoryId && formId) {
          try {
            const schema = await getFieldsSchema(categoryId, formId);
            setIncidentData(prev => ({
              ...prev,
              fieldsSchema: schema
            }));
            
            // Si hay custom_fields en el schema, cargar sus valores desde field_values
            if (schema && schema.custom_fields && task.field_values) {
              const fieldValuesMap = {};
              task.field_values.forEach(fv => {
                if (fv.field && fv.field.id) {
                  // Buscar el field en el schema por su ID
                  const fieldInSchema = schema.custom_fields.find(({field}) => field.id === fv.field.id);
                  if (fieldInSchema) {
                    fieldValuesMap[fieldInSchema.field.id] = fv.value;
                  }
                }
              });
              
              // Actualizar incidentData con los valores de los campos personalizados
              Object.keys(fieldValuesMap).forEach(fieldId => {
                setIncidentData(prev => ({
                  ...prev,
                  [fieldId]: fieldValuesMap[fieldId]
                }));
              });
            }
          } catch (error) {
            console.error('Error al cargar schema:', error);
          }
        }
      } catch (error) {
        console.error('Error al cargar incidente para editar:', error);
        showError('Error al cargar el incidente para editar');
        navigate('/admin/questions/task/incidentes');
      } finally {
        setLoadingIncident(false);
      }
    };

    loadIncidentForEdit();
  }, [isEditMode, editId, navigate, getFieldsSchema, showError]);

  // Resetear form cuando cambia la categoria
  useEffect(() => {
    if (incidentData.category_id) {
      setIncidentData(prev => ({
        ...prev,
        form_id: null
      }));
      setErrors(prev => ({
        ...prev,
        form_id: ''
      }));
    }
  }, [incidentData.category_id]);


  // Guards removidos - no interrumpir el flujo con confirmaciones

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!incidentData.contact_id) {
        newErrors.contact_id = 'Debe seleccionar o crear un contacto';
      }
    }
    
    if (step === 2) {
      if (!incidentData.category_id) {
        newErrors.category_id = 'Debe seleccionar una categoría';
      }
      if (!incidentData.form_id) {
        newErrors.form_id = 'Debe seleccionar un formulario';
      }
      if (!incidentData.channel_id) {
        handleInputChange('channel_id', 'web');
      }
      // Verificar que el schema esté cargado según el documento
      if (incidentData.loadingFields) {
        newErrors.fieldsSchema = 'Cargando campos adicionales...';
      }
      if (incidentData.errorFields) {
        newErrors.fieldsSchema = incidentData.errorFields;
      }
    }
    
    if (step === 3) {
      if (incidentData.fieldsSchema && incidentData.fieldsSchema.sections) {
        const sections = incidentData.fieldsSchema.sections;
        const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
        sortedSections.forEach((section, idx) => {
          (section.fields || []).forEach(fieldDef => {
            if (fieldDef.visible === false) return;
            if (!fieldDef.required) return;
            const key = `section_${idx}_field_${fieldDef.field.id}`;
            const val = incidentData[key];
            if (!val || val === '') {
              newErrors[key] = `${fieldDef.field.name} es requerido`;
            }
          });
        });
      } else if (incidentData.fieldsSchema && incidentData.fieldsSchema.fields) {
        incidentData.fieldsSchema.fields.forEach(field => {
          const fieldValue = incidentData[field.id];
          if (field.required && (!fieldValue || fieldValue === '')) {
            newErrors[field.id] = `${field.label} es requerido`;
          }
        });
      }
    }
    
    if (step === 4) {
      // El responsable es obligatorio
      if (!assignedTo) {
        newErrors.assigned_to = 'Debe seleccionar un responsable';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((field, value) => {
    setIncidentData(prev => {
      if (typeof field === 'string' && field.includes('.'))  {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleContactSelect = (contact) => {
    setIncidentData(prev => ({
      ...prev,
      contact_id: contact.id,
      selectedContact: contact
    }));
    setErrors(prev => ({ ...prev, contact_id: '' }));
    // Avanzar automáticamente al paso 2
    setTimeout(() => {
      setCurrentStep(2);
    }, 300);
  };

  const handleContactCreate = useCallback((contact) => {
    setIncidentData(prev => ({
      ...prev,
      contact_id: contact.id,
      selectedContact: contact
    }));
    setShowCreateDrawer(false);
    setErrors(prev => ({ ...prev, contact_id: '' }));
    
    // Avanzar automáticamente al siguiente paso
    setTimeout(() => {
      setCurrentStep(2);
    }, 500); // Pequeño delay para que se vea la selección
  }, []);

  // Callbacks estables para ContactSearchBar
  const handleSearchChange = useCallback((value) => {
    handleInputChange('searchQuery', value);
    // Resetear hasSearched y limpiar resultados cuando cambia el query
    setHasSearched(false);
    handleInputChange('searchResults', []);
  }, [handleInputChange]);

  const handleSearchResults = useCallback((results) => {
    handleInputChange('searchResults', results);
    // Solo marcar como buscado si el query tiene al menos 3 caracteres
    if (incidentData.searchQuery.trim().length >= 3) {
      setHasSearched(true);
    }
  }, [handleInputChange, incidentData.searchQuery]);

  const handleSearching = useCallback((isSearching) => {
    handleInputChange('isSearching', isSearching);
  }, [handleInputChange]);

  const handleCreateIncident = async () => {
    // En modo edición, solo validar que haya un status seleccionado
    if (isEditMode) {
      if (!statusId) {
        showError('Debe seleccionar un estado');
        return;
      }
    } else {
      // En modo creación, validar el paso 4 (que incluye la validación del responsable)
      if (!validateStep(4)) return;
    }
    
    setIsSubmitting(true);
    try {
      if (isEditMode && editId) {
        const updatedTask = {
          ...originalTask,
          title: incidentData.title || originalTask.title || '',
          description: incidentData.description || originalTask.description || '',
          status: statusId,
          priority: priorityId,
          assigned_to: assignedTo
        };
        
        await incidentsService.updateIncident(editId, { 
          title: incidentData.title, 
          description: incidentData.description 
        }, updatedTask, statusId);
        success(`Tarea actualizada exitosamente`);
        // Redirigir al detalle del incidente actualizado
        setTimeout(() => {
          navigate(`/admin/questions/task/incidentes/${editId}`);
        }, 1000);
        return;
      }

      const custom_fields = {};
      
      if (incidentData.fieldsSchema && incidentData.fieldsSchema.sections) {
        const sections = incidentData.fieldsSchema.sections;
        const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
        sortedSections.forEach((section, idx) => {
          (section.fields || []).forEach(fieldDef => {
            if (fieldDef.visible === false) return;
            const key = `section_${idx}_field_${fieldDef.field.id}`;
            const val = incidentData[key];
            if (val !== undefined && val !== null && val !== '') {
              custom_fields[fieldDef.field.id] = val;
            }
          });
        });
      } else if (incidentData.fieldsSchema && incidentData.fieldsSchema.custom_fields) {
        incidentData.fieldsSchema.custom_fields.forEach(({field}) => {
          const fieldValue = incidentData[field.id];
          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
            custom_fields[field.id] = fieldValue;
          }
        });
      }

      const incidentPayload = {
        contact_id: incidentData.contact_id,
        form_id: incidentData.form_id || incidentData.fieldsSchema?.form_id,
        category_id: incidentData.category_id,
        canal_codigo: incidentData.channel_id || 'web',
        // values: values,
        custom_fields: custom_fields, // ✅ Agregar custom_fields
        fieldsSchema: incidentData.fieldsSchema, 
        // Mantener compatibilidad con formato anterior
        description: incidentData.description,
        title: incidentData.title,
        category_name: incidentData.category_name || '',
        form_name: incidentData.form_name || '',
        assigned_to: assignedTo || null // Agregar responsable asignado
      };

      if (incidentData.requiresLocation && incidentData.location.route) {
        // Normalizar km: convertir coma a punto y asegurar que sea numérico
        const normalizedLocation = {
          ...incidentData.location,
          km: parseFloat(incidentData.location.km.replace(',', '.'))
        };
        incidentPayload.location = normalizedLocation;
      }

      // Crear incidente
      const response = await incidentsService.createIncident(incidentPayload);
      success(`Incidente creado exitosamente: ${response.id}`);
      // Redirigir al listado de incidentes después de un breve delay
      setTimeout(() => {
        navigate(`/admin/questions/task/incidentes/${response.id}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error al crear incidente:', error);
      
      // Manejar diferentes tipos de errores
      if (error.type === 'validation' && error.errors) {
        // Errores de validación 422 - mapear por campo
        const newErrors = {};
        Object.keys(error.errors).forEach(field => {
          newErrors[field] = error.errors[field];
        });
        setErrors(newErrors);
        
        // Enfocar el primer campo con error
        const firstErrorField = Object.keys(newErrors)[0];
        if (firstErrorField) {
          setTimeout(() => {
            const element = document.querySelector(`[name="${firstErrorField}"], #${firstErrorField}`);
            if (element) {
              element.focus();
            }
          }, 100);
        }
        
        showError('Por favor corrija los errores en el formulario');
      } else {
        // Otros errores
        const errorMessage = error.message || 'Error al crear el incidente. Intente nuevamente.';
        showError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || loadingIncident) {
    return (
      <Layout>
        <div className="incidentes-container">
          <div className="container">
            <div className="incidentes-header">
              <Skeleton width="250px" height="35px" borderRadius="8px" className="mb-8px" />
              <Skeleton width="450px" height="18px" borderRadius="6px" />
            </div>

            <div className="incidentes-layout">
              <div className="stepper">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="step">
                    <Skeleton width="30px" height="30px" borderRadius="50%" variant="circular" />
                  </div>
                ))}
              </div>

              <div className="form-container">
                <Skeleton width="220px" height="24px" borderRadius="8px" className="mb-16px" />
                
                {/* Skeleton de campos del formulario */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <Skeleton width="150px" height="16px" borderRadius="6px" className="mb-8px" />
                  <Skeleton width="100%" height="44px" borderRadius="8px" />
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <Skeleton width="180px" height="16px" borderRadius="6px" className="mb-8px" />
                  <Skeleton width="100%" height="44px" borderRadius="8px" />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <Skeleton width="200px" height="16px" borderRadius="6px" className="mb-8px" />
                  <Skeleton width="100%" height="100px" borderRadius="8px" />
                </div>

                {/* Skeleton de botones */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                  <SkeletonButton width="120px" height="42px" />
                  <SkeletonButton width="120px" height="42px" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="incidentes-container">
        <div className="container">
          <div className="incidentes-header">
            <h1>{isEditMode ? 'Editar Incidente' : 'Alta de Incidente'}</h1>
            <p>{isEditMode ? 'Modifique los campos que desee actualizar' : 'Complete los siguientes pasos para crear un nuevo incidente'}</p>
          </div>

          <div className="incidentes-layout">
            {!isEditMode && (
              <div className="stepper">
                <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">Contacto</div>
                </div>
                <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">Clasificación</div>
                </div>
                <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                  <div className="step-number">3</div>
                  <div className="step-label">Datos Específicos</div>
                </div>
                <div className={`step ${currentStep >= 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
                  <div className="step-number">4</div>
                  <div className="step-label">Asignar Responsable</div>
                </div>
                <div className={`step ${currentStep >= 5 ? 'active' : ''} ${currentStep > 5 ? 'completed' : ''}`}>
                  <div className="step-number">5</div>
                  <div className="step-label">Confirmación</div>
                </div>
              </div>
            )}

            <div className={`form-container ${isEditMode ? 'edit-mode' : ''}`}>
            {/* Modo edición: solo permitir modificar el status */}
            {isEditMode ? (
              <IncidentEditForm
                incidentData={incidentData}
                handleInputChange={handleInputChange}
                loadingStatuses={loadingStatuses}
                statuses={statuses}
                statusId={statusId}
                setStatusId={setStatusId}
                loadingPriorities={loadingPriorities}
                priorities={priorities}
                priorityId={priorityId}
                setPriorityId={setPriorityId}
                loadingUsers={loadingUsers}
                users={users}
                assignedTo={assignedTo}
                setAssignedTo={setAssignedTo}
                navigate={navigate}
                editId={editId}
                handleCreateIncident={handleCreateIncident}
                isSubmitting={isSubmitting}
              />
            ) : (
              /* Modo creación: mostrar pasos individuales */
              <>
              {currentStep === 1 && (
              <div className="form-step">
                <h3 className="step-title">Paso 1 - Contacto</h3>
                
                <ContactSearchBar
                  searchQuery={incidentData.searchQuery}
                  onSearchChange={handleSearchChange}
                  onSearchResults={handleSearchResults}
                  onSearching={handleSearching}
                  onShowCreateDrawer={() => setShowCreateDrawer(true)}
                />

                {/* Mostrar contacto seleccionado si hay uno */}
                {incidentData.selectedContact && (
                  <div className="selected-contact-card" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>✅ Contacto seleccionado</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <p style={{ margin: 0 }}><strong>Nombre:</strong> {incidentData.selectedContact.first_name || 'No especificado'}</p>
                          <p style={{ margin: 0 }}><strong>Apellido:</strong> {incidentData.selectedContact.last_name || 'No especificado'}</p>
                          <p style={{ margin: 0 }}><strong>CUIL:</strong> {incidentData.selectedContact.cuil || 'No especificado'}</p>
                          {incidentData.selectedContact.email && (
                            <p style={{ margin: 0 }}><strong>Email:</strong> {incidentData.selectedContact.email}</p>
                          )}
                          {incidentData.selectedContact.phone && (
                            <p style={{ margin: 0 }}><strong>Teléfono 1:</strong> {incidentData.selectedContact.phone}</p>
                          )}
                          {incidentData.selectedContact.phone2 && (
                            <p style={{ margin: 0 }}><strong>Teléfono 2:</strong> {incidentData.selectedContact.phone2}</p>
                          )}
                          {incidentData.selectedContact.address && (
                            <p style={{ margin: 0 }}><strong>Dirección:</strong> {incidentData.selectedContact.address}</p>
                          )}
                        </div>
                      </div>
                      <button 
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          handleInputChange('contact_id', null);
                          handleInputChange('selectedContact', null);
                        }}
                        style={{ fontSize: '0.875rem' }}
                      >
                        Cambiar
                      </button>
                    </div>
                  </div>
                )}

                <ContactResultsList
                  results={incidentData.searchResults}
                  isSearching={incidentData.isSearching}
                  onContactSelect={handleContactSelect}
                  onShowCreateDrawer={() => setShowCreateDrawer(true)}
                  hasSearched={hasSearched}
                />

                {errors.contact_id && <span className="error-message">{errors.contact_id}</span>}
              </div>
            )}

            {currentStep === 2 && (
              <div className="form-step">
                <h3 className="step-title">Paso 2 - Clasificación</h3>
                
                <IncidentClassification
                  catalogs={catalogs}
                  incidentData={incidentData}
                  onInputChange={handleInputChange}
                  getCategoriesByParent={getCategoriesByParent}
                  getFormsByCategory={getFormsByCategory}
                  getFieldsSchema={getFieldsSchema}
                  errors={errors}
                />
              </div>
            )}

            {currentStep === 3 && (
              <div className="form-step">
                <h3 className="step-title">Paso 3 - Datos Específicos</h3>

                {incidentData.fieldsSchema?.initial_message && (
                  <div
                    style={{
                      marginBottom: '1rem',
                      padding: '0.75rem 1rem',
                      border: '1px solid #d8dde6',
                      borderRadius: '8px',
                      backgroundColor: '#f8fafc'
                    }}
                    dangerouslySetInnerHTML={{ __html: incidentData.fieldsSchema.initial_message }}
                  />
                )}
                
                {incidentData.fieldsSchema && incidentData.fieldsSchema.sections ? (
                  <SectionCards
                    sections={incidentData.fieldsSchema.sections}
                    values={incidentData}
                    onChange={handleInputChange}
                    errors={errors}
                    completedSections={completedSections}
                    onSectionComplete={(idx, completed) => {
                      setCompletedSections(prev => ({ ...prev, [idx]: completed }));
                    }}
                  />
                ) : incidentData.fieldsSchema && incidentData.fieldsSchema.custom_fields ? (
                  <div className="dynamic-fields-section">
                    <FormRenderer
                      schema={incidentData.fieldsSchema}
                      values={incidentData}
                      onChange={handleInputChange}
                      errors={errors}
                    />
                  </div>
                ) : null}

                <div className="form-group">
                  <label htmlFor="title">Título</label>
                  <textarea
                    id="title"
                    value={incidentData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Ingrese un título del caso"
                    rows="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Descripción</label>
                  <textarea
                    id="description"
                    value={incidentData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Ingrese una descripción del caso"
                    rows="4"
                  />
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="form-step">
                <h3 className="step-title">Paso 4 - Asignar Responsable</h3>
                
                <div className="form-group">
                  <label htmlFor="assigned_to">
                    Responsable a cargo de la tarea <span style={{ color: '#d32f2f' }}>*</span>
                  </label>
                  {loadingUsers ? (
                    <div style={{ padding: '0.5rem', color: '#666' }}>Cargando usuarios...</div>
                  ) : (
                    <select
                      id="assigned_to"
                      value={assignedTo || ''}
                      onChange={(e) => {
                        const value = e.target.value || null;
                        setAssignedTo(value);
                        if (errors.assigned_to) {
                          setErrors(prev => ({ ...prev, assigned_to: '' }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: errors.assigned_to ? '1px solid #d32f2f' : '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="">Seleccione un responsable</option>
                      {Array.isArray(users) && users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} {user.email ? `(${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.assigned_to && (
                    <span className="error-message" style={{ display: 'block', marginTop: '0.5rem', color: '#d32f2f', fontSize: '0.875rem' }}>
                      {errors.assigned_to}
                    </span>
                  )}
                  {users.length === 0 && !loadingUsers && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px', fontSize: '0.875rem', color: '#856404' }}>
                      ⚠️ No se encontraron usuarios disponibles
                    </div>
                  )}
                  <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                    Solo usuarios de grupos asociados al proyecto del formulario.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="form-step">
                <h3 className="step-title">Paso 5 - Confirmación</h3>
                
                <IncidentSummary
                  incidentData={incidentData}
                  catalogs={catalogs}
                  assignedTo={assignedTo}
                  users={users}
                />
              </div>
            )}

              <div className="form-actions">
                {currentStep > 1 && (
                  <button type="button" className="btn btn-secondary" onClick={prevStep}>
                    Anterior
                  </button>
                )}
                
                {currentStep < 5 ? (
                  // No mostrar botón "Siguiente" en paso 1 (avanza automáticamente al seleccionar contacto)
                  currentStep > 1 ? (
                    <button type="button" className="btn btn-primary" onClick={nextStep}>
                      Siguiente
                    </button>
                  ) : null
                ) : (
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={handleCreateIncident}
                    disabled={isSubmitting}
                  >
                    {isSubmitting 
                      ? (isEditMode ? 'Actualizando...' : 'Creando...') 
                      : (isEditMode ? 'Actualizar Incidente' : 'Crear Incidente')}
                  </button>
                )}
              </div>
              </>
            )}
          </div>
          </div>
        </div>

        {showCreateDrawer && (
          <ContactCreateDrawer
            onClose={() => setShowCreateDrawer(false)}
            onContactCreated={handleContactCreate}
          />
        )}
        
        {/* Toast Container */}
        <ToastContainer 
          toasts={toasts} 
          onRemoveToast={removeToast}
          position="top-right"
        />

        {/* Loading Overlay cuando se está creando el incidente */}
        {isSubmitting && (
          <div className="loading-overlay">
            <div className="loading-content">
              <Skeleton width="80px" height="80px" borderRadius="50%" variant="circular" />
              <h3>Creando tarea...</h3>
              <p>Por favor espere</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const Incidentes = () => {
  return (
    <CatalogsProvider>
      <IncidentesContent />
    </CatalogsProvider>
  );
};

export default Incidentes;
